import clsx from 'clsx';

import { normalizeBuyerIntentId } from 'components/BuyerIntent';
import ClickoutWrapper from 'components/ClickoutWrapper';
import CtaButton from 'components/CtaButton';
import Grid from 'components/Grid';
import Icon from 'components/Icon';
import LastUpdatedDate from 'components/LastUpdatedDate';
import Link from 'components/Link';
import Save from 'components/Listing/components/Buttons/components/Save';
import Logo from 'components/Logo';
import Rating from 'components/Rating';
import Typography from 'components/Typography';
import { EventContext } from 'context/EventContext';
import ServerViewport from 'context/ViewportContext/ServerViewport';
import { addEvent } from 'lib/AnalyticsGDM';

import PricingPPLWrapper from '../PricingPPLWrapper';

import classes from './ListingInfo.module.scss';

const getCtaDetails = ({ isPublishedAlternative, ctaUrls, isPremium }) => {
  const hasAlternatives =
    isPublishedAlternative && !!ctaUrls?.cta?.endsWith?.('/alternatives/');
  const baseEvent = {
    name: isPremium
      ? 'engagement_product_clickout'
      : 'engagement_product_click',
    engagementComponent: 'product_main-card',
  };

  return {
    text: isPremium
      ? 'visit website'
      : hasAlternatives
        ? 'see alternatives'
        : 'learn more',
    event: {
      ...baseEvent,
      engagementDestination: `go-to_${
        isPremium
          ? 'vendor-website'
          : hasAlternatives
            ? 'product_alternatives'
            : 'product_details'
      }`,
      engagementDetails: `button_${isPremium ? 'visit-website' : hasAlternatives ? 'see-alternatives' : 'learn-more'}`,
    },
  };
};

const RenderRating = ({ ctaUrls, ratingAvg, numReviews, reviews }) => (
  <ServerViewport
    is="sm"
    renderIf={
      <Rating.Link
        href={ctaUrls.reviews}
        average={ratingAvg}
        totals={numReviews}
        isCompact={true}
        ratings={reviews?.by_rating}
        ratingsHrefs={reviews?.by_rating_urls}
      />
    }
    renderOr={
      <Rating.Link
        href={ctaUrls.reviews}
        average={ratingAvg}
        totals={numReviews}
        ratings={reviews?.by_rating}
        ratingsHrefs={reviews?.by_rating_urls}
      />
    }
  />
);

/** @type {import("./types").default} */
const ListingInfo = ({
  listingInfoData,
  isCompact = false,
  hideLastUpdated = false,
  head,
  hasRichContent = false,
  buyerIntentMainCtaSourceLocation = 'product-info-card:main-cta',
}) => {
  const {
    cta_urls,
    is_premium,
    logo_url,
    name,
    rating_avg,
    num_reviews,
    tagline,
    last_updated_on,
    reviews,
  } = listingInfoData;

  const isPublishedAlternative =
    !!listingInfoData.publishedPages?.includes('alternatives');
  const hideCTAButton = !(isPublishedAlternative || is_premium);
  const ctaDetails = getCtaDetails({
    isPublishedAlternative,
    ctaUrls: cta_urls,
    isPremium: is_premium,
  });

  let headDataLayer = head?.data_layer;

  if (typeof headDataLayer !== 'undefined') {
    headDataLayer = head?.data_layer[0];
  }

  const buyerIntentProductId = normalizeBuyerIntentId(
    listingInfoData.global_product_id || headDataLayer?.globalProductId
  );
  const pplCategoryId =
    headDataLayer?.categoryPageUuid ||
    listingInfoData.ppl_category?.global_category_id;

  return (
    <Grid
      data-testid="listing-info"
      container={true}
      className={clsx('ListingInfo', classes.root, classes.displayColumnOnXs, {
        [classes.isCompact]: isCompact,
      })}
    >
      <EventContext
        value={{
          engagementComponent: 'product_main-card',
        }}
      >
        <Grid item={true} className={classes.content}>
          <div className={classes.logoContainer}>
            <Logo
              className={classes.logo}
              src={logo_url}
              alt={`${name} Logo`}
            />
          </div>
          <div>
            <ClickoutWrapper
              isClickout={is_premium}
              url={is_premium ? cta_urls.cta : undefined}
              buyerIntentProductId={buyerIntentProductId}
              buyerIntentSourceLocation="product-info-card:name"
              buyerIntentTrackAdClick={is_premium}
              {...(is_premium && {
                ...addEvent({
                  name: 'engagement_product_clickout',
                  engagementComponent: 'product_main-card',
                  engagementDestination: 'go-to_vendor-website',
                  engagementDetails: 'text-link_product-name',
                }),
              })}
            >
              <Link
                data-testid="listing-name"
                className={clsx(classes.listingName, {
                  [classes.listingNameNoLink]: !is_premium,
                  [classes.listingNameLink]: is_premium,
                })}
                variant="heading-sm"
              >
                {name}
                {is_premium && (
                  <Icon
                    className={classes.externalLinkIcon}
                    icon={['far', 'external-link']}
                    size="12"
                  />
                )}
              </Link>
            </ClickoutWrapper>
            {!isCompact && (
              <>
                <div
                  className={classes.ratingContainer}
                  data-testid="product_info_card_v2-listing_info-rating_container"
                >
                  <RenderRating
                    ctaUrls={cta_urls}
                    ratingAvg={rating_avg}
                    numReviews={num_reviews}
                    reviews={reviews}
                  />
                </div>
                <Typography
                  className={classes.tagline}
                  variant="body-xs"
                  data-testid="listing-tagline"
                >
                  {tagline}
                </Typography>
                {!(hasRichContent || hideLastUpdated) && (
                  <LastUpdatedDate lastUpdatedDate={last_updated_on} />
                )}
              </>
            )}
          </div>
        </Grid>
        <Grid
          item={true}
          className={clsx(classes.ctaButtons, classes.displayInXs)}
        >
          <div className={classes.ctaButtonGroup}>
            <Save
              listing={listingInfoData}
              onlyIcon={true}
              variant="outlined"
              className={classes.saveButton}
            />
            <div className={classes.mainCta} data-testid="listing-main-cta">
              {(head.hasPplCategory || head.hasLiveChatEnabled) &&
              !is_premium ? (
                <PricingPPLWrapper
                  globalProductId={headDataLayer?.globalProductId}
                  globalCategoryId={pplCategoryId}
                  hasPplExitIntentEnabled={head?.hasPplExitIntentEnabled}
                  buyerIntentSourceLocation="product-info-card:get-price"
                >
                  <CtaButton
                    fullWidth={true}
                    {...addEvent({
                      engagementComponent: 'product_main-card',
                      engagementDestination: 'go-to_ppl-price-form',
                      engagementDetails: 'button_get_price',
                      name: 'engagement_product_click',
                    })}
                  >
                    Get Price
                  </CtaButton>
                </PricingPPLWrapper>
              ) : hideCTAButton ? null : (
                <ClickoutWrapper
                  url={cta_urls?.cta}
                  isClickout={is_premium}
                  buyerIntentProductId={buyerIntentProductId}
                  buyerIntentSourceLocation={buyerIntentMainCtaSourceLocation}
                  buyerIntentTrackAdClick={is_premium}
                  {...addEvent(ctaDetails.event)}
                >
                  <CtaButton fullWidth={true} component="span">
                    {ctaDetails.text}
                    {is_premium && (
                      <Icon
                        className={classes.ctaButtonExternalLinkIcon}
                        icon={['far', 'external-link']}
                        size="16"
                      />
                    )}
                  </CtaButton>
                </ClickoutWrapper>
              )}
            </div>
          </div>
        </Grid>
      </EventContext>
    </Grid>
  );
};

export default ListingInfo;
